import { type FC, useMemo } from "react";

import { type UserWithMembership, useAuthorsFromGithub } from "@moment/state";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, cn } from "@moment/ui";

export interface AuthorSelectProps {
	value: string | undefined;
	onChange: (value: string | undefined) => void;
	members: UserWithMembership[];
	disabled?: boolean;
	placeholder?: string;
	className?: string;
}

export const AuthorSelect: FC<AuthorSelectProps> = ({
	value,
	onChange,
	members,
	disabled = false,
	placeholder = "Select author...",
	className,
}) => {
	// Fetch the current author's info if they're not in the members list
	const { author: fetchedAuthor, isLoading } = useAuthorsFromGithub(value ?? "");

	// Combine members with the fetched author (if not already in members)
	const allAuthors = useMemo(() => {
		// If the current value is already in members, just return members
		if (!value || members.some((m) => m.user_id === value)) {
			return members;
		}

		// If we fetched author info, add them to the list
		if (fetchedAuthor) {
			const fetchedMember: UserWithMembership = {
				user_id: value,
				name: fetchedAuthor.displayName ?? fetchedAuthor.githubUsername ?? "",
				nickname: fetchedAuthor.githubUsername ?? "",
				picture: fetchedAuthor.profileImageUrl ?? "",
				relation: "member" as const,
			};
			return [...members, fetchedMember];
		}

		// If still loading or no data, return members as-is
		return members;
	}, [members, value, fetchedAuthor]);

	return (
		<Select
			value={value ?? ""}
			onValueChange={(v) => onChange(v || undefined)}
			disabled={disabled}
		>
			<SelectTrigger className={cn("w-full", className)}>
				<SelectValue placeholder={placeholder}>
					{value ? (
						<AuthorDisplay members={allAuthors} userId={value} isLoading={isLoading} />
					) : (
						<span className="text-tertiary">{placeholder}</span>
					)}
				</SelectValue>
			</SelectTrigger>
			<SelectContent>
				{allAuthors.map((member) => (
					<SelectItem key={member.user_id} value={member.user_id}>
						<div className="flex items-center gap-2">
							{member.picture ? (
								<img
									src={member.picture}
									alt={member.name}
									className="h-5 w-5 rounded-full"
								/>
							) : (
								<div className="h-5 w-5 rounded-full bg-palette-gray-6 flex items-center justify-center text-xs">
									{member.name?.charAt(0) ?? "?"}
								</div>
							)}
							<span>{member.name}</span>
							{member.nickname && (
								<span className="text-tertiary">@{member.nickname}</span>
							)}
						</div>
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);
};

const AuthorDisplay: FC<{
	members: UserWithMembership[];
	userId: string;
	isLoading?: boolean;
}> = ({ members, userId, isLoading }) => {
	const member = members.find((m) => m.user_id === userId);

	if (isLoading) {
		return <span className="text-tertiary">Loading...</span>;
	}

	if (!member) {
		return <span className="text-tertiary">{userId}</span>;
	}

	return (
		<div className="flex items-center gap-2">
			{member.picture ? (
				<img src={member.picture} alt={member.name} className="h-5 w-5 rounded-full" />
			) : (
				<div className="h-5 w-5 rounded-full bg-palette-gray-6 flex items-center justify-center text-xs">
					{member.name?.charAt(0) ?? "?"}
				</div>
			)}
			<span>{member.name}</span>
		</div>
	);
};
